import { bookFormatLabel } from "../../utils/bookFormatLabel.js";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Slider from "@mui/joy/Slider";
import Switch from "@mui/joy/Switch";
import LinearProgress from "@mui/joy/LinearProgress";
import Chip from "@mui/joy/Chip";

// ─── Компонент строки настройки ───────────────────────────────────────────────

function SettingRow({ label, hint, children }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography level="body-sm" sx={{ fontWeight: 500 }}>{label}</Typography>
        {hint && (
          <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>{hint}</Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{children}</Box>
    </Stack>
  );
}

// ─── Модальное окно экспорта ──────────────────────────────────────────────────

export default function ModalExport({ open, onClose, onExport, bookSize, pagesCount, isExporting, exportProgress }) {
  const [quality, setQuality] = useState(2);       // px ratio: 1=черновик, 2=стандарт, 3=высокое
  const [includeBleed, setIncludeBleed] = useState(false);
  const [colorSpace, setColorSpace] = useState("rgb");
  const [filename, setFilename] = useState("photobook");

  const qualityLabels = { 1: "Черновик", 2: "Стандарт", 3: "Высокое" };
  const qualityDescriptions = {
    1: "Быстро, меньший размер файла",
    2: "Оптимальный баланс качества",
    3: "Максимум — большой файл",
  };

  const estimatedPages = pagesCount * 2; // разворот = 2 стр.

  const handleExport = () => {
    onExport({ quality, includeBleed, colorSpace, filename });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="export-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(10,6,30,0.72)",
            backdropFilter: "blur(6px)",
            padding: "16px",
          }}
        >
          <motion.div
            key="export-dialog"
            initial={{ opacity: 0, scale: 0.88, y: 32 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.3, ease: [0.34, 1.3, 0.64, 1] }}
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: 20,
              background: "var(--joy-palette-background-surface)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.38), 0 8px 24px rgba(87,42,195,0.15)",
              scrollbarWidth: "none",
            }}
          >
            <Box sx={{ p: { xs: 3, sm: 4 } }}>

              {/* Заголовок */}
              <Stack alignItems="center" gap={0.5} mb={3}>
                <Typography level="h3" sx={{ fontFamily: "display", color: "primary.400", letterSpacing: "0.04em" }}>
                  📄 Экспорт PDF
                </Typography>
                <Typography level="title-lg" sx={{ fontWeight: 700, textAlign: "center" }}>
                  Настройки экспорта
                </Typography>
                <Stack direction="row" gap={1} mt={0.5}>
                  {bookSize && (
                    <Chip size="sm" variant="soft" color="primary">
                      {bookFormatLabel(bookSize)}
                    </Chip>
                  )}
                  <Chip size="sm" variant="soft" color="neutral">
                    {pagesCount} разворот{pagesCount > 1 ? "ов" : ""} · {estimatedPages} стр.
                  </Chip>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              {/* Настройки */}
              <Stack gap={2.5} sx={{ mb: 3 }}>

                {/* Имя файла */}
                <SettingRow label="Имя файла" hint="Без расширения, в названии появится .pdf">
                  <Box
                    component="input"
                    value={filename}
                    onChange={e => setFilename(e.target.value)}
                    sx={{
                      width: 160,
                      px: 1.5, py: 0.75,
                      borderRadius: "8px",
                      border: "1.5px solid",
                      borderColor: "var(--joy-palette-divider)",
                      bgcolor: "var(--joy-palette-background-level1)",
                      color: "var(--joy-palette-text-primary)",
                      fontSize: "13px",
                      outline: "none",
                      fontFamily: "inherit",
                      "&:focus": {
                        borderColor: "var(--joy-palette-primary-400)",
                      },
                    }}
                  />
                </SettingRow>

                {/* Качество */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Box>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>Качество рендера</Typography>
                      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {qualityDescriptions[quality]}
                      </Typography>
                    </Box>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={quality === 3 ? "success" : quality === 2 ? "primary" : "neutral"}
                    >
                      {qualityLabels[quality]}
                    </Chip>
                  </Stack>
                  <Slider
                    value={quality}
                    onChange={(_, v) => setQuality(v)}
                    min={1} max={3} step={1}
                    color="primary" size="sm"
                    marks={[
                      { value: 1, label: "1×" },
                      { value: 2, label: "2×" },
                      { value: 3, label: "3×" },
                    ]}
                    sx={{ "--Slider-trackSize": "3px", "--Slider-thumbSize": "14px", mt: 1 }}
                  />
                </Box>

                {/* Цветовое пространство */}
                <SettingRow label="Цветовое пространство" hint="CMYK рекомендуется для печати">
                  <Select
                    size="sm"
                    value={colorSpace}
                    onChange={(_, v) => setColorSpace(v)}
                    sx={{ minWidth: 120 }}
                  >
                    <Option value="rgb">RGB (экран)</Option>
                    <Option value="cmyk">CMYK (печать)</Option>
                  </Select>
                </SettingRow>

                {/* Вылет */}
                <SettingRow
                  label="Вылет 3мм"
                  hint="Добавляет поля для типографской обрезки"
                >
                  <Switch
                    checked={includeBleed}
                    onChange={e => setIncludeBleed(e.target.checked)}
                    color="primary"
                    size="sm"
                  />
                </SettingRow>

              </Stack>

              <Divider sx={{ mb: 3 }} />

              {/* Прогресс экспорта */}
              {isExporting && (
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography level="body-sm" sx={{ fontWeight: 500, color: "primary.500" }}>
                      ⏳ Генерация PDF...
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                      {Math.round(exportProgress)}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    determinate
                    value={exportProgress}
                    color="primary"
                    sx={{ borderRadius: "4px", height: "6px" }}
                  />
                  <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.75, textAlign: "center" }}>
                    Рендерим страницы в высоком качестве...
                  </Typography>
                </Box>
              )}

              {/* Кнопки */}
              <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="neutral"
                  size="lg"
                  onClick={onClose}
                  disabled={isExporting}
                  sx={{ minWidth: 120 }}
                >
                  Отмена
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  size="lg"
                  onClick={handleExport}
                  loading={isExporting}
                  sx={{ minWidth: 200, fontWeight: 700 }}
                >
                  {isExporting ? "Экспортирую..." : "📥 Экспортировать PDF"}
                </Button>
              </Stack>

            </Box>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}